/**
 * Shared pieces of the hotspot map: key names, the pressure bar, the
 * outcome and direction chips, the trend sparkline and the row's
 * "why" note. State is always carried by text; bars and lines are
 * decorative.
 */
import type {
  HotspotBy,
  HotspotCoverage,
  HotspotDirection,
  HotspotFindingDeltas,
  HotspotOutcome,
  HotspotRow,
  HotspotTrendPoint,
} from "@iamu/shared";
import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import { Chip } from "@/components/ui/chip";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export const KEY_LABEL: Record<HotspotBy, string> = {
  capability: "Capability",
  human_test: "Human test",
  scenario: "Scenario",
  persona: "Persona",
  route: "Page",
};

/** Plural noun for the rows of a key ("capabilities"). */
export const KEY_NOUN: Record<HotspotBy, string> = {
  capability: "capabilities",
  human_test: "human tests",
  scenario: "scenarios",
  persona: "personas",
  route: "pages",
};

export const formatPressure = (p: number | null) =>
  p == null ? "—" : p.toFixed(1);

export const plural = (n: number, word: string, many = `${word}s`) =>
  `${n} ${n === 1 ? word : many}`;

export function coveragePercent(c: HotspotCoverage): number {
  return c.total > 0 ? Math.round((100 * c.keyed) / c.total) : 0;
}

/**
 * Decorative bar next to the (accessible) pressure number. Clear areas
 * (walked, nothing unresolved) show an empty track.
 */
export function PressureBar({
  row,
  max,
  thin,
  className,
}: {
  row: Pick<HotspotRow, "pressure" | "status">;
  max: number;
  thin?: boolean;
  className?: string;
}) {
  const h = thin ? "h-1.5" : "h-2";
  if (row.pressure == null || max <= 0) {
    return (
      <span
        aria-hidden="true"
        className={cn("block rounded-full bg-muted/40", h, className)}
      />
    );
  }
  const width = `${Math.max(4, Math.round((100 * row.pressure) / max))}%`;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "block w-full overflow-hidden rounded-full bg-muted/60",
        h,
        className,
      )}
    >
      {row.status === "measured" && (
        <span
          className={cn(
            "block rounded-full bg-mark-critical/80 transition-all",
            h,
          )}
          style={{ width }}
        />
      )}
    </span>
  );
}

/** Human-test outcome: "0/2 pass", with ● all / ◐ some / ○ none. */
export function OutcomeChip({ outcome }: { outcome: HotspotOutcome | null }) {
  if (!outcome) {
    return <span className="text-sm text-muted-foreground">No tests</span>;
  }
  const total = outcome.pass + outcome.partial + outcome.fail;
  const tone =
    outcome.pass === total
      ? "helps"
      : outcome.fail > 0
        ? "critical"
        : "important";
  // ● all pass · ◐ mixed or partial · ○ all "no"
  const mark =
    outcome.pass === total ? "●" : outcome.fail === total ? "○" : "◐";
  const detail = [
    outcome.partial > 0 && `${outcome.partial} partially`,
    outcome.fail > 0 && `${outcome.fail} no`,
  ]
    .filter(Boolean)
    .join(", ");
  const chip = (
    <Chip
      tone={tone}
      tabIndex={detail ? 0 : undefined}
      className={
        detail
          ? "cursor-help focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          : undefined
      }
    >
      <span aria-hidden="true">{mark}</span>
      {outcome.pass}/{total} pass
    </Chip>
  );
  return detail ? <SimpleTooltip content={detail}>{chip}</SimpleTooltip> : chip;
}

const DIRECTION = {
  better: { label: "Better", tone: "helps", Icon: ArrowDownRight },
  worse: { label: "Worse", tone: "critical", Icon: ArrowUpRight },
  flat: { label: "Flat", tone: "neutral", Icon: ArrowRight },
} as const;

/** Trend direction as text; "—" with fewer than two runs. */
export function DirectionChip({
  direction,
}: {
  direction: HotspotDirection | null;
}) {
  if (!direction) {
    return (
      <SimpleTooltip content="Fewer than 2 runs">
        <span
          role="button"
          tabIndex={0}
          className="text-sm text-muted-foreground cursor-help focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          —<span className="sr-only">fewer than 2 runs</span>
        </span>
      </SimpleTooltip>
    );
  }
  const d = DIRECTION[direction];
  return (
    <Chip tone={d.tone}>
      <d.Icon aria-hidden="true" />
      {d.label}
    </Chip>
  );
}

/** Per-run values as a small line; decorative (values are in text). */
export function Sparkline({
  points,
  className,
}: {
  points: HotspotTrendPoint[];
  className?: string;
}) {
  const W = 64;
  const H = 20;
  if (points.length === 0) return null;
  const max = Math.max(...points.map((p) => p.value), 1);
  const x = (i: number) =>
    points.length === 1 ? W / 2 : 2 + (i * (W - 4)) / (points.length - 1);
  const y = (v: number) => H - 2 - (v / max) * (H - 4);
  const path = points.map((p, i) => `${x(i)},${y(p.value)}`).join(" ");
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      className={cn("shrink-0 text-foreground", className)}
    >
      {points.length > 1 && (
        <polyline
          points={path}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      )}
      {points.map((p, i) => (
        <circle
          key={p.walkthroughId}
          cx={x(i)}
          cy={y(p.value)}
          r={i === points.length - 1 ? 2.5 : 1.5}
          fill="currentColor"
        />
      ))}
    </svg>
  );
}

/** Why a row reads the way it does (never "healthy" without evidence). */
export function statusNote(row: HotspotRow): string | null {
  if (row.status === "not_walked") return "Not walked in this window";
  if (row.status === "clear") {
    return `No unresolved problems in ${plural(row.exposureSteps, "step")}`;
  }
  if (row.openProblems === 0 && row.awaitingRerun > 0) {
    return `${plural(row.awaitingRerun, "fix", "fixes")} awaiting re\u2011run`;
  }
  if (
    row.openProblems === 0 &&
    row.awaitingRerun === 0 &&
    row.outcome &&
    row.outcome.fail > 0
  ) {
    return `${plural(row.outcome.fail, "human test")} failing`;
  }
  return null;
}

/** "7 open · 1 recurring · 1 awaiting re-run" */
export function problemSummary(row: HotspotRow): string {
  const parts = [`${row.openProblems} open`];
  if (row.recurring > 0) parts.push(`${row.recurring} recurring`);
  if (row.awaitingRerun > 0)
    parts.push(`${row.awaitingRerun} awaiting re\u2011run`);
  if (row.regressed > 0) parts.push(`${row.regressed} regressed`);
  return parts.join(" · ");
}

/**
 * Micro telemetry: finding-level deltas (+new, −fixed, ⚠ regressed).
 */
export function FindingDeltaBadges({
  deltas,
  className,
}: {
  deltas?: HotspotFindingDeltas | null;
  className?: string;
}) {
  if (!deltas) return null;
  const hasDeltas =
    deltas.regressed > 0 ||
    deltas.new > 0 ||
    deltas.fixed > 0 ||
    deltas.fixDidntHold > 0;
  if (!hasDeltas) return null;

  return (
    <div className={cn("inline-flex flex-wrap items-center gap-1", className)}>
      {deltas.regressed > 0 && (
        <Chip tone="critical">
          <span aria-hidden="true">⚠</span>
          {deltas.regressed} regressed
        </Chip>
      )}
      {deltas.fixDidntHold > 0 && (
        <Chip tone="critical">{deltas.fixDidntHold} fix didn&apos;t hold</Chip>
      )}
      {deltas.new > 0 && <Chip tone="neutral">+{deltas.new} new</Chip>}
      {deltas.fixed > 0 && <Chip tone="helps">−{deltas.fixed} fixed</Chip>}
    </div>
  );
}
